/**
 * Glitch bars + vertical tear lines — scanline-style distortion effects.
 */

import type { Graphics } from 'pixi.js';
import { VOID } from '../../constants';
import type { VoidState } from './types';
import { visibleX, lerp } from './types';

export function randomizeGlitchBars(count: number, state: VoidState): void {
  const spread = state.riseIntensity > 0.3 ? VOID.GLITCH_SPREAD_RISE : 10;
  const v = visibleX();
  state.glitchBars = [];
  for (let i = 0; i < count; i++) {
    state.glitchBars.push({
      x: v.off + Math.random() * v.w,
      width: VOID.GLITCH_WIDTH_MIN + Math.random() * (VOID.GLITCH_WIDTH_MAX - VOID.GLITCH_WIDTH_MIN),
      yOffset: (Math.random() - 0.5) * spread * 2,
      phase: Math.random() * Math.PI * 2,
      color: Math.random() < 0.7 ? VOID.EDGE_COLOR : 0x8800ff,
    });
  }
}

export function updateGlitch(gfx: Graphics, dt: number, voidY: number, state: VoidState): void {
  const glitchRefresh = state.riseIntensity > 0.1
    ? lerp(VOID.GLITCH_REFRESH, VOID.GLITCH_REFRESH_RISE, state.riseIntensity)
    : VOID.GLITCH_REFRESH;
  const glitchCount = state.riseIntensity > 0.1
    ? Math.round(lerp(VOID.GLITCH_COUNT, VOID.GLITCH_COUNT_RISE, state.riseIntensity))
    : VOID.GLITCH_COUNT;
  const glitchAlpha = state.riseIntensity > 0.1
    ? lerp(VOID.GLITCH_ALPHA, VOID.GLITCH_ALPHA_RISE, state.riseIntensity)
    : VOID.GLITCH_ALPHA;

  state.glitchTimer += dt;
  if (state.glitchTimer >= glitchRefresh) {
    state.glitchTimer = 0;
    randomizeGlitchBars(glitchCount, state);
  }

  // Draw glitch bars
  gfx.clear();
  const time = Date.now() * 0.001;
  for (const bar of state.glitchBars) {
    const sineOffset = Math.sin(time * VOID.GLITCH_CYCLE_SPEED + bar.phase) * VOID.GLITCH_SINE_AMP;
    const alphaPulse = 0.5 + Math.sin(time * 2 + bar.phase * 3) * 0.5;
    const y = voidY + bar.yOffset + sineOffset;
    gfx.rect(bar.x, y, bar.width, VOID.GLITCH_HEIGHT);
    gfx.fill({ color: bar.color, alpha: glitchAlpha * alphaPulse });
  }

  // Vertical tear lines (during high intensity)
  if (state.riseIntensity > 0.3) {
    const tv = visibleX();
    const tearCount = Math.round(VOID.GLITCH_TEAR_COUNT * state.riseIntensity);
    for (let i = 0; i < tearCount; i++) {
      const tx = tv.off + Math.random() * tv.w;
      const tearH = VOID.GLITCH_TEAR_HEIGHT_MIN + Math.random() * (VOID.GLITCH_TEAR_HEIGHT_MAX - VOID.GLITCH_TEAR_HEIGHT_MIN);
      const ty = voidY - tearH * 0.5 - Math.random() * 20;
      const tearAlpha = 0.3 + state.riseIntensity * 0.4;
      gfx.rect(tx, ty, 2, tearH);
      gfx.fill({ color: VOID.EDGE_COLOR, alpha: tearAlpha * Math.random() });
    }
  }
}
